import React from 'react';
import styled from 'styled-components';
import { List as AntList } from 'antd';
import VirtualList from 'rc-virtual-list';
import { windowMaxWidth } from '../Layout/breakpoints';
import { isEmptyArray } from '../../../helpers/utility';

const LIST_ITEM_HEIGHT = 50;

const StyledList = styled(AntList)`
  background: none;
  color: var(--greyscale-shade);
  font-family: var(--font-family-regular);
  font-size: var(--font-size-regular) !important;
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-medium);
  overflow-y: hidden;
  white-space: nowrap;

  .ant-list-item:not(.item-header),
  .ant-list-item-meta-description,
  .ant-list-item-meta-title {
    font-size: var(--font-size-regular) !important;
  }

  @media ${windowMaxWidth.splitScreen} {
    overflow-y: auto;
  }

  .ant-list-header {
    height: 65px;
    padding-block: 10px;
  }

  &.scrollable-list {
    .ant-spin-container {
      max-height: calc(100vh - 247px);
      min-height: 400px;
      overflow-y: auto;

      ul.ant-list-items {
        padding-bottom: 30px;
      }
    }
  }

  /* stylelint-disable-next-line no-descending-specificity */
  .ant-list-item {
    cursor: ${({ $listItemsClickable }) => ($listItemsClickable ? 'pointer' : 'default')};
    height: ${LIST_ITEM_HEIGHT}px;
    line-height: var(--font-line-height-medium);
    padding: 15px 40px;

    &:hover {
      background: ${({ $listItemsClickable }) =>
        $listItemsClickable ? 'var(--greyscale-porcelain) !important' : 'none'};
      transition: ${({ $listItemsClickable }) => ($listItemsClickable ? 'background 0.5s' : 'none')};
    }

    &:last-child {
      border-bottom: 1px solid var(--greyscale-dark-white);
    }

    &.list-item-no-hover-effect {
      cursor: auto;
      padding: 10px 30px;

      &:hover {
        background: ${({ $listItemsClickable }) =>
          $listItemsClickable ? 'var(--greyscale-white) !important' : 'none'};
        transition: ${({ $listItemsClickable }) => ($listItemsClickable ? 'background 0.5s' : 'none')};
      }
    }

    .ant-list-item-action {
      align-items: center;
      display: flex;
      margin-left: 0;

      li {
        display: flex;
        padding: 0;

        &:not(:last-child) {
          margin-right: 20px;
        }

        @media ${windowMaxWidth.splitScreen} {
          &:not(:last-child) {
            margin-right: 10px;
          }
        }

        button {
          padding: 0;

          .ant-btn-icon {
            margin-inline-end: 6px !important;
          }

          span:not(.ant-btn-icon) {
            display: flex;
          }

          &.attachments-modal-list-item {
            flex: unset;
          }

          @media ${windowMaxWidth.splitScreen} {
            .ant-btn-icon {
              margin-inline-end: 0 !important;
            }

            span:not(.ant-btn-icon) {
              display: none;
            }
          }
        }
      }

      .ant-list-item-action-split {
        display: none;
      }
    }

    &.not-selectable {
      cursor: default;
      padding: 10px 30px;

      &:hover {
        background: var(--greyscale-white) !important;
      }
    }

    @media ${windowMaxWidth.mobile} {
      height: auto;
    }
  }

  .ant-list-empty-text {
    color: var(--greyscale-concrete);
    font-size: var(--font-size-regular);
    font-weight: var(--font-weight-regular);
    line-height: var(--font-line-height-regular);
    margin: 0;
    padding: 10px;
    white-space: normal;
  }

  .rc-virtual-list-holder {
    max-height: none !important;

    @media ${windowMaxWidth.mobile} {
      height: calc(100dvh - 245px) !important;
    }
  }

  &.linked-items-list {
    .ant-list-item {
      @media ${windowMaxWidth.mobile} {
        padding: 10px 20px;
      }
    }
  }

  &.list-left-column {
    .ant-list-item {
      color: var(--greyscale-shade);
      font-weight: var(--font-weight-regular);
      height: 60px;
      margin: 0;
      padding: 10px 20px !important;

      &.item-selected {
        background: var(--primary-oqio-blue) !important;
        color: var(--greyscale-white) !important;
        font-weight: var(--font-weight-medium);
      }

      &.item-header {
        background: var(--secondary-whisper);
        color: var(--greyscale-blackish);
        cursor: default;
        font-size: var(--font-size-small);
        font-stretch: normal;
        font-style: normal;
        font-weight: var(--font-weight-medium);
        height: 40px;
        letter-spacing: var(--font-letter-spacing-regular);
        line-height: var(--font-line-height-medium);
        margin: 0;
        padding: 10px 20px !important;
        text-transform: uppercase;

        &:hover {
          background: var(--secondary-whisper) !important;
        }
      }

      &:hover {
        color: var(--greyscale-blackish);
      }
    }
  }

  &.claims-list {
    /* stylelint-disable-next-line no-descending-specificity */
    .ant-list-item {
      align-items: flex-start !important;
      gap: 10px;
      height: auto;
      min-height: 90px;
      padding: 20px 30px !important;
      position: relative;

      /* stylelint-disable-next-line no-descending-specificity */
      span {
        display: flex;
      }

      &:not(:last-child) {
        border-bottom: 1px solid var(--greyscale-dark-white);
      }

      &.last-visited-item {
        background-color: var(--secondary-whisper);
      }

      @media ${windowMaxWidth.mobile} {
        padding: 15px 10px !important;
        margin: 0 10px;
        justify-content: flex-start;
        gap: 5px 10px;
      }
    }

    @media ${windowMaxWidth.mobile} {
      margin: 0;
    }
  }

  &.existing-claims-list {
    max-height: 198px;

    /* stylelint-disable-next-line no-descending-specificity */
    .ant-list-item {
      align-items: flex-start;
      display: flex;
      height: 66px;

      @media ${windowMaxWidth.mobile} {
        height: auto;
        padding: 15px 20px;
      }
    }

    @media ${windowMaxWidth.mobile} {
      max-height: 297px;
    }
  }

  &.policies-list {
    /* stylelint-disable-next-line no-descending-specificity */
    .ant-list-item {
      align-items: flex-start !important;
      gap: 20px;
      height: auto;
      min-height: 81px;
      padding: 20px 30px !important;
      position: relative;

      /* stylelint-disable-next-line no-descending-specificity */
      span {
        display: flex;
      }

      &:not(:last-child) {
        border-bottom: 1px solid var(--greyscale-dark-white);
      }

      &.last-visited-item {
        background-color: var(--secondary-whisper);
      }

      @media ${windowMaxWidth.mobile} {
        padding: 15px 10px !important;
        margin: 0 10px;
        min-height: auto;
      }
    }

    @media ${windowMaxWidth.mobile} {
      margin: 0;
    }
  }

  &.policy-details-claims-list {
    /* stylelint-disable-next-line no-descending-specificity */
    .ant-list-item {
      align-items: flex-start !important;
      border-bottom: 1px solid var(--greyscale-dark-white);
      height: auto;
      margin: 0;
      min-height: 72px;
      padding: 10px 30px !important;

      &:last-child {
        border-bottom: 1px solid var(--greyscale-dark-white);
      }

      &:hover {
        background: none !important;
        cursor: auto;
      }

      @media (${windowMaxWidth.mobile}) {
        height: auto;
        padding: 10px 20px !important;
      }
    }
  }
`;

// For the virtual list to work, the virtualListHeight prop must be passed
const List = ({
  virtual,
  virtualListHeight,
  dataSource,
  renderItem,
  itemHeight = LIST_ITEM_HEIGHT,
  itemKey,
  $listItemsClickable = true,
  ...otherProps
}) => {
  if (virtual) {
    return (
      <StyledList {...otherProps} $listItemsClickable={$listItemsClickable}>
        {!isEmptyArray(dataSource) && (
          <VirtualList data={dataSource} height={virtualListHeight} itemHeight={itemHeight} itemKey={itemKey}>
            {(item) => renderItem(item)}
          </VirtualList>
        )}
      </StyledList>
    );
  }
  return (
    <StyledList
      dataSource={dataSource}
      renderItem={renderItem}
      {...otherProps}
      $listItemsClickable={$listItemsClickable}
    />
  );
};

List.Item = StyledList.Item;

export default List;
